import React from 'react';
import Dialog from '../../../components/Dialog';
import { IconFlag } from '../../../components/dialogIcons';
import { CLOSURE_REASON } from '../../../lib/tasks';

/*
  Saying a location has closed, and what was found there.

  Ported onto the shared dialog shell on 5 September 2026, to Group E's approved
  drawing `dialog-closure-*.png`.

  **The reasons it wrote were words nothing else in OnHand could read.** This
  dialog offered `business_closed`, `lost_account`, `moved_location`,
  `no_longer_using` and `other`. The Tasks screen and the Review Hub translate
  `practice_closed`, `moved_locations` and `no_longer_stocks`, which are also
  the three every closure in the database actually holds. So a closure a real
  rep flagged came back on an administrator's screen as "business closed",
  through a fallback that just takes the underscores out of whatever it was
  given. **Two vocabularies for one field, and neither knew about the other.**

  There are three now, from `src/lib/tasks.js`, which is the one list. Anything
  that is none of the three goes in the notes, in the rep's own words, which is
  where an administrator reads it anyway.

  **The labels are the drawing's, in plain English.** "Reason for Closure *" and
  "Inventory Retrieved?" became "Why is it closing" and "Was our stock
  retrieved", because the person filling this in is standing in a doorway
  looking at a papered-over window, not filling in a form for a database.
*/
export default function ClosureModal({ count, form, onChange, onConfirm, onClose }) {
  const ready = !!form.reason;
  return (
    <Dialog
      kind="danger" icon={<IconFlag />}
      title="Flag this location as closed"
      record={count?.warehouse?.name}
      says="An admin confirms it before anything changes."
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-quiet" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={!ready}>Flag as closed</button>
        </>
      }
    >
      <div className="form-lbl" style={{ marginTop: 0 }}>Why is it closing</div>
      <select className="form-sel" value={form.reason} onChange={e => onChange('reason', e.target.value)}>
        <option value="">Pick a reason</option>
        {Object.entries(CLOSURE_REASON).map(([code, label]) => (
          <option key={code} value={code}>{label}</option>
        ))}
      </select>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <div className="form-lbl">Last counted</div>
          <input type="date" className="form-inp" value={form.last_count_date}
            onChange={e => onChange('last_count_date', e.target.value)} />
        </div>
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          {/* Two buttons rather than a picker, from the drawing: it is a yes or
              a no, and a picker for two answers is a tap and a scroll for
              nothing. */}
          <div className="form-lbl">Was a final count done</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {[['yes', 'Yes'], ['no', 'No']].map(([v, label]) => (
              <button key={v} type="button" onClick={() => onChange('final_count_performed', v)}
                className={'btn ' + (form.final_count_performed === v ? 'btn-primary' : 'btn-quiet')}
                style={{ flex: 1, minHeight: 44 }}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="form-lbl">Was our stock retrieved</div>
      <select className="form-sel" value={form.inventory_retrieved} onChange={e => onChange('inventory_retrieved', e.target.value)}>
        <option value="">Pick one</option>
        <option value="yes">Yes, all of it</option>
        <option value="partial">Some of it</option>
        <option value="no">No, it is still there</option>
      </select>

      <div className="form-lbl">Notes</div>
      <textarea className="form-ta" value={form.notes} onChange={e => onChange('notes', e.target.value)}
        placeholder="Anything an admin should know" />
    </Dialog>
  );
}
